import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import GridItem from '../../../layout/GridComponents/GridItem';
import ExportItem from './subComponents/ExportItem.component';
import { AxiosInstance } from '../../../../utilities/requestClient';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useDispatch, useSelector } from 'react-redux';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';
import useTitleBarDatePicker from '../../../../hooks/useTitleBarDatePicker';
import TokenService from '../../../../services/token-service';
import { getAllRelevantDates } from '../../../../utilities/dateUtilities';
import { DatePicker } from '../../../../types/redux/pages/PagesStore';
import dayjs from 'dayjs';
import { addAdditionalTitleBarComponents, removeTitleBarComponent, toggleHideStandardDatePicker } from '../../../../redux/pages/actions';
import { APPEND_NEW_DATE_PICKER_TO_TITLEBAR, REMOVE_DATE_PICKER_FROM_TITLEBAR } from '../../../../types/redux/pages/pageTypes';
import FundDateExportItem from './subComponents/FundDateExportItem.component';

export interface SelectOption {
  id: string;
  title: string;
  options: string[];
}

export interface RaptorExportFund {
  fund_id: string;
  fund_name: string;
  fund_name_full: string;
  position_dates?: string[];
}

export interface RaptorExportUrl {
  name: string;
  url: string;
  description: string;
  file_type: string;
  link_type: 'link' | 'select' | 'fund-date';
  category: string;
  select_options?: SelectOption[];
  funds?: RaptorExportFund[];
}

export const fetchRaptorExportUrls = async (clientId: string,): Promise<RaptorExportUrl[]> => {
  const response = await AxiosInstance.get<RaptorExportUrl[]>(
    `/get_raptor_export_links/${clientId}`,
  );
  return response.data;
};

export function useRaptorExportUrls(clientId: string | null) {
  return useQuery<RaptorExportUrl[]>({
    queryKey: ['raptor-export-urls', clientId],
    queryFn: () => {
      return fetchRaptorExportUrls(clientId!);
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
}


function slugifyFileName(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

const GeneralRaptorExport: React.FC<GeneralOverviewPageRenderedComponentProps> = (
  props,
) => {
  const dispatch = useDispatch();
  const clientId = TokenService.getUser()?.user_client_id || null;

  const { data, isLoading, isError, isFetching } = useRaptorExportUrls(clientId);
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);

  const reportDate = useTitleBarDatePicker('reportDate');

  // Use reportDate from titlebar, otherwise fall back to props or default
  const positionDate = reportDate || props.positionDate || new Date().toISOString().split('T')[0];

  // Helper function to append raptorPlatformId to download URLs if it exists
  const getDownloadUrl = (baseUrl: string) => {
    if (raptorPlatformId) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}raptor_platform_id=${raptorPlatformId}`;
    }
    return baseUrl;
  };

  // Use custom date picker with all dates for all clients
  useEffect(() => {
    // Hide the standard position date picker when using custom report date picker
    dispatch(toggleHideStandardDatePicker(true));

    const allDates = getAllRelevantDates();

    const datePickersToAdd: DatePicker[] = [
      {
        displayOnOverviewPage: true,
        titleBarKey: 'reportDate',
        titleBarTitle: 'Report Date:',
        currentValue: dayjs().format('YYYY-MM-DD'),
        availableDates: allDates,
      },
    ];
    dispatch(
      addAdditionalTitleBarComponents(
        datePickersToAdd,
        APPEND_NEW_DATE_PICKER_TO_TITLEBAR,
      ),
    );

    return () => {
      dispatch(
        removeTitleBarComponent('reportDate', REMOVE_DATE_PICKER_FROM_TITLEBAR),
      );
      // Restore the standard position date picker when component unmounts
      dispatch(toggleHideStandardDatePicker(false));
    };
  }, [dispatch]);

  return (
    <QueryBoundary
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      data={data}
      loadingMessages={['Loading export options...']}
      errorMessage="There was an error loading the available exports. Please try again later."
      noDataMessage="No exports are currently configured."
    >
      {(data) => {
        // Group items by category
        const groupedByCategory = data.reduce((acc, item) => {
          const category = item.category || 'Other';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(item);
          return acc;
        }, {} as Record<string, RaptorExportUrl[]>);

        // Get unique categories
        const categories = Object.keys(groupedByCategory);

        return (
          <>
            {categories.map((category) => {
              const categoryId = `raptor-exports-${slugifyFileName(category)}`;
              return (
                <GridItem key={category} xs={12} card cardStyle={{ padding: '1.25rem' }}>
                  <section aria-labelledby={categoryId}>
                    <header className="mb-4">
                      <h2
                        id={categoryId}
                        className="text-[1.88rem] font-semibold text-[#003B71] mb-2"
                      >
                        {category}
                      </h2>
                    </header>

                    <ul className="flex flex-wrap gap-5 list-none p-0 m-0">
                      {groupedByCategory[category].map((item) => {
                        const safeLabel = slugifyFileName(item.name);

                        // Handle select type links differently
                        if (item.link_type === 'select' && item.select_options) {
                          // For select links, append the current positionDate as a path segment
                          const downloadUrl = `${item.url}/${positionDate}`;
                          const fileName = `${safeLabel}-{positionDate}.${item.file_type}`;

                          // Transform select_options to the format expected by ExportItem
                          const options = item.select_options.map((selectOption) => ({
                            type: 'single-select' as const,
                            id: selectOption.id,
                            title: selectOption.title,
                            options: selectOption.options,
                          }));

                          return (
                            <li key={item.url} className="min-w-[260px]">
                              <ExportItem
                                title={`${item.name} (.${item.file_type})`}
                                description={item.description}
                                options={options}
                                downloadUrl={getDownloadUrl(downloadUrl)}
                                fileName={fileName}
                                openDownloadInNewTab
                              />
                            </li>
                          );
                        } else if (item.link_type === 'fund-date') {
                          return (
                            <FundDateExportItem
                              key={item.url}
                              item={item}
                              funds={item.funds}
                            />
                          );
                        } else if (item.link_type === 'link') {
                          // Regular links
                          const downloadUrl = `${item.url}/${positionDate}`;
                          const fileName = `${safeLabel}-${positionDate}.${item.file_type}`;

                          return (
                            <li key={item.url} className="min-w-[260px]">
                              <ExportItem
                                title={`${item.name} (.${item.file_type})`}
                                description={item.description}
                                downloadUrl={getDownloadUrl(downloadUrl)}
                                fileName={fileName}
                                openDownloadInNewTab
                              />
                            </li>
                          );
                        }
                        return null;
                      })}
                    </ul>
                  </section>
                </GridItem>
              );
            })}
          </>
        );
      }}
    </QueryBoundary>
  );
};

export default GeneralRaptorExport;
